import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Drawer, Input, Button, Icon, Form, Spin, List, Row, Col } from "ant-design-vue";
import {
	ConversationTemplateService,
	EntityDetailsModel,
	EntityItemModel,
	UtteranceEntitiesModel
} from "@/services/conversation-templates";
import { CommonEntitiesFormLocale, CommonLocale } from "@/locales/localeid";
import { fmtMsg, Action, ActionItem, MessageHelper, NotificationType, ServerExceptionCode } from "commonui";
import { replaceMultipleWhiteSpace } from "@/util/func";
import { CLUValidations } from "@/util/constants";

const nameMaxLen = 50;
interface EntityItemEditModel {
	id: number;
	value: string;
	index: number;
	isInEditMode: boolean;
	isValid: boolean;
	validationMessage: string;
}
@Component({
	components: {
		Drawer,
		TextArea: Input.TextArea,
		Button,
		Icon,
		Input,
		Spin,
		Form,
		FormItem: Form.Item,
		Action,
		ActionItem,
		List,
		ListItem: List.Item,
		Row,
		Col
	}
})
export default class CommonEntitiesForm extends Vue {
	@Prop({ default: 0 }) entityId!: number;
	@Prop() onCommonEntitiesFormClose!: (reloadRequired: boolean) => void;
	@Prop({ default: false }) isDrawerVisible!: boolean;
	@Prop({ default: [] }) commonEntities!: UtteranceEntitiesModel[];
	public loading = false;
	private templateService = ConversationTemplateService();
	public locale = CommonEntitiesFormLocale;
	public commonLocale = CommonLocale;
	public format = fmtMsg;
	private entityData?: EntityDetailsModel;
	public commonEntityForm;
	public entityItems: EntityItemEditModel[] = [];
	public enableSaveButton: boolean = false;

	onEditCommonEntity() {
		this.enableSaveButton = true;
	}

	created() {
		this.commonEntityForm = this.$form.createForm(this, { name: "common-entities-form" });
	}

	mounted() {
		if (this.entityId > 0) {
			this.getEntityData();
		}
	}

	setFormData(entityData?: EntityDetailsModel) {
		if (entityData) {
			this.commonEntityForm.getFieldDecorator("name", {
				initialValue: entityData.name,
				preserve: true
			});

			const entityItems: EntityItemEditModel[] = [];
			let current = 0;
			entityData.items.forEach((e) => {
				entityItems.push({
					id: e.id,
					index: current,
					isInEditMode: false,
					value: e.value,
					isValid: true,
					validationMessage: ""
				});
				current = current + 1;
			});

			this.entityItems = entityItems;
		}
	}

	get isSaveButtonDisable() {
		return this.entityItems.filter((e) => e.isInEditMode).length > 0;
	}

	get drawerTitle() {
		return this.entityId > 0
			? this.format(this.locale.CommonEntitiesFormEditTitle)
			: this.format(this.locale.CommonEntitiesFormCreateTitle);
	}

	getEntityData() {
		this.loading = true;
		this.templateService
			.getEntityDetails(this.entityId)
			.then((data) => {
				this.entityData = data;
				this.setFormData(data);
				this.loading = false;
			})
			.catch(({ error_code, error_description }) => {
				this.showErrorMessage({ error_code, error_description });
				this.loading = false;
				this.onCommonEntitiesFormClose(false);
			});
	}

	saveEditEntityData(entityDetails: EntityDetailsModel) {
		this.loading = true;
		this.templateService
			.saveUpdateEntity(entityDetails)
			.then((data) => {
				this.loading = false;
				MessageHelper.Message(
					NotificationType.Success,
					this.format(this.locale.CommonEntitiesFormSavedSuccessMessage)
				);
				this.onCommonEntitiesFormClose(true);
			})
			.catch((response) => {
				if (response.body) {
					const { error_code, error_description } = response.body;
					this.showErrorMessage({ error_code, error_description });
				}
				this.loading = false;
			});
	}

	scrollItemsArea() {
		this.$nextTick(() => {
			const items: any = this.$refs.listSection;
			if (items) {
				items.scrollTop = items.scrollHeight;
			}
		});
	}

	onItemChange(e: Event & { target: { value: any } }, index: number) {
		const currentItem = this.entityItems.find((e) => e.index === index);
		const value: any = e.target.value;

		if (currentItem) {
			const retValue = this.validateItems(value, currentItem, index);

			if (!retValue) {
				return;
			}

			this.entityItems = this.entityItems.map((e) => {
				if (e.index === index) {
					e.isValid = true;
					e.validationMessage = "";
				}
				return e;
			});
		}
	}

	validateItems(value: string, currentItem, index) {
		if (value.trim() === "") {
			this.markItemInvalid(this.format(this.locale.CommonEntitiesFormItemsNameRequiredMessage), index);
			return false;
		}

		const similarItems = this.entityItems.filter(
			(e) =>
				replaceMultipleWhiteSpace(e.value).toLocaleLowerCase().trim() ===
				replaceMultipleWhiteSpace(currentItem.value).toLocaleLowerCase().trim()
		);

		if (similarItems.length > 1) {
			this.markItemInvalid(
				this.format(this.locale.CommonEntitiesFormDuplicateEntityItemMessage, {
					items: replaceMultipleWhiteSpace(currentItem.value)
				}),
				index
			);

			return false;
		}

		return true;
	}

	markItemInvalid(msg: string, index: number) {
		this.entityItems = this.entityItems.map((e) => {
			if (e.index === index) {
				e.isValid = false;
				e.validationMessage = msg;
			}
			return e;
		});
	}

	onAddEntityItem() {
		if (this.isSaveButtonDisable) {
			return;
		}
		const maxIndex =
			this.entityItems.length > 0 ? this.entityItems.reduce((p, c) => (p.index > c.index ? p : c)).index : 0;
		const newEntityItem: EntityItemEditModel = {
			id: 0,
			index: maxIndex + 1,
			value: "",
			isInEditMode: true,
			isValid: true,
			validationMessage: ""
		};
		this.entityItems.push(newEntityItem);
		this.scrollItemsArea();
	}

	onEditEntityItem(index: number) {
		if (this.isSaveButtonDisable) {
			return;
		}
		this.entityItems = this.entityItems.map((e) => {
			if (e.index === index) {
				e.isInEditMode = true;
			}
			return e;
		});
	}

	onSaveEntityItem(index: number) {
		this.onEditCommonEntity();
		const currentItem = this.entityItems.find((e) => e.index === index);

		if (currentItem) {
			if (!currentItem.isValid) {
				return;
			}

			const retValue = this.validateItems(currentItem.value, currentItem, index);
			if (!retValue) {
				return;
			}

			this.entityItems = this.entityItems.map((e) => {
				if (e.index === index) {
					e.value = replaceMultipleWhiteSpace(e.value);
					e.isInEditMode = false;
					e.isValid = true;
					e.validationMessage = "";
				}
				return e;
			});
		}
	}

	get nameMaxValue() {
		return nameMaxLen;
	}

	uniqueTextValidator(rule: any, value: string, callback: (msg?: string) => void) {
		if (!value || value === "") {
			callback();
			return;
		}
		const data: UtteranceEntitiesModel[] =
			this.entityId > 0 ? this.commonEntities.filter((c) => c.id !== this.entityId) : this.commonEntities;

		if (
			data.some(
				(d) => d.name.trim().toLocaleLowerCase() === replaceMultipleWhiteSpace(value).trim().toLocaleLowerCase()
			)
		) {
			callback(fmtMsg(this.locale.CommonEntitiesFormNameAlreadyExistMessage));
			return;
		}

		callback();
	}

	entityNameValidator(rule: any, value: string, callback: (msg?: string) => void) {
		const isInvalid = CLUValidations.invalidIntentNameRegex.test(value);
		if (isInvalid) {
			callback(fmtMsg(this.locale.CommonEntitiesFormNameNotValidMessage));
			return;
		}
		callback();
	}

	showErrorMessage({ error_code, error_description }) {
		switch (error_code) {
			case ServerExceptionCode.TargetHasExistedException:
				MessageHelper.Message(
					NotificationType.Failed,
					this.format(this.locale.CommonEntitiesFormNameAlreadyExistMessage)
				);
				break;
			case ServerExceptionCode.CommonEntityNotExistException:
				MessageHelper.Message(
					NotificationType.Failed,
					this.format(this.locale.CommonEntitiesFormItemsEntityNotExistMessage)
				);
				break;
			case ServerExceptionCode.CommonEntityItemsNotExistException:
				MessageHelper.Message(
					NotificationType.Failed,
					this.format(this.locale.CommonEntitiesFormItemsRequiredMessage)
				);
				break;
			case ServerExceptionCode.CommonEntityItemDuplicateException:
				MessageHelper.Message(
					NotificationType.Failed,
					this.format(this.locale.CommonEntitiesFormDuplicateEntityItemMessage)
				);
				break;
			case ServerExceptionCode.CommonEntityNameLengthException:
				MessageHelper.Message(
					NotificationType.Failed,
					this.format(this.locale.CommonEntitiesFormNameLengthMessage, { len: this.nameMaxValue })
				);
				break;
			case ServerExceptionCode.ArgumentIsNullOrInvalidException:
				MessageHelper.Message(
					NotificationType.Failed,
					this.format(this.commonLocale.CommonHttpArgumentIsNullOrInvalid)
				);
				break;
		}
	}

	onRemoveEntityItem(index: number) {
		this.onEditCommonEntity();
		this.entityItems = this.entityItems.filter((e) => e.index !== index);
	}

	onDrawerClose() {
		this.onCommonEntitiesFormClose(false);
	}

	validateAndSubmitForm(e: any) {
		this.commonEntityForm.validateFields((err, values) => {
			if (!err) {
				//Check Entity Items
				if (this.entityItems.length == 0) {
					MessageHelper.Message(
						NotificationType.Warning,
						this.format(this.locale.CommonEntitiesFormItemsRequiredMessage)
					);
					return;
				}

				const allItems = this.entityItems.map((i) =>
					replaceMultipleWhiteSpace(i.value).toLocaleLowerCase().trim()
				);

				const repeatedItems: string[] = [];
				allItems.forEach((i) => {
					const items = allItems.filter((a) => a === i);
					if (items.length > 1 && !repeatedItems.includes(i)) {
						repeatedItems.push(i);
					}
				});

				if (repeatedItems.length > 0) {
					MessageHelper.Message(
						NotificationType.Warning,
						this.format(this.locale.CommonEntitiesFormDuplicateEntityItemMessage, {
							items: repeatedItems.join()
						})
					);
					return;
				}

				let current = 1;
				const entityDataToSave: EntityDetailsModel = {
					id: this.entityId,
					name: replaceMultipleWhiteSpace(values.name),
					items: this.entityItems.map((e) => {
						const itemDetails: EntityItemModel = {
							id: e.id,
							value: replaceMultipleWhiteSpace(e.value),
							order: current
						};
						current = current + 1;
						return itemDetails;
					})
				};

				this.saveEditEntityData(entityDataToSave);
			}
		});
	}
}
